import { isDefined } from "@clipboard-health/util-ts";
import { Box, MenuItem, type OutlinedTextFieldProps } from "@mui/material";
import { type ReactNode } from "react";

import { TextInput } from "./TextInput";

export interface SelectOption {
  /** The value reported to `onChange` and matched against Select's `value`. */
  value: string;
  /** What the user sees in the closed control and in the menu. */
  label: ReactNode;
  disabled?: boolean;
}

export interface SelectProps extends Pick<
  OutlinedTextFieldProps,
  | "label"
  | "fullWidth"
  | "size"
  | "disabled"
  | "error"
  | "helperText"
  | "required"
  | "autoFocus"
  | "id"
  | "name"
  | "sx"
  | "inputProps"
  | "InputLabelProps"
> {
  options: SelectOption[];
  value: string | undefined;
  onChange: (value: string) => void;
  /** Shown with disabled-text styling while nothing is selected */
  placeholder?: string;
}

/** Berlin design-system dropdown based on TextInput's styling */
export function Select(props: SelectProps) {
  const { options, value, onChange, placeholder, InputLabelProps, ...rest } = props;

  const hasPlaceholder = isDefined(placeholder);

  return (
    <TextInput
      {...rest}
      select
      value={value}
      // label must float up front — otherwise it overlaps the placeholder text.
      InputLabelProps={hasPlaceholder ? { shrink: true, ...InputLabelProps } : InputLabelProps}
      SelectProps={{
        displayEmpty: hasPlaceholder,
        renderValue: (selected) => {
          const selectedOption = options.find((option) => option.value === selected);
          if (!isDefined(selectedOption)) {
            // Render as a bare span that only overrides color, so the
            // placeholder only overrides the color
            return isDefined(placeholder) ? (
              <Box component="span" sx={(theme) => ({ color: theme.palette.text.disabled })}>
                {placeholder}
              </Box>
            ) : null;
          }

          return selectedOption.label;
        },
      }}
      onChange={(event) => {
        onChange(event.target.value);
      }}
    >
      {options.map((option) => (
        <MenuItem key={option.value} disabled={option.disabled} value={option.value}>
          {option.label}
        </MenuItem>
      ))}
    </TextInput>
  );
}
